import { useState, useContext, useEffect } from 'react';
import './SidePanel.css';
import Download from '../../icons/Download.svg?react';
import Upload from '../../icons/Upload.svg?react';
import ProcessedData from '../../icons/ProcessedData.svg?react';
import CalendarSetting from '../../icons/CalendarSetting.svg?react';
import ImportMeshPanel from './ImportMeshPanel';
import DataSelectionPanel from './DataSelectionPanel';
import MenuExport from './ExportMeshPanel';
import ExtractionSettingsPanel from './ExtractionSettingsPanel';
import Typography from '@mui/material/Typography';
import MikeSidePanel from '../mike-sidepanel/MikeSidePanel';
import MikeSidePanelNavigation from '../mike-sidepanel-navigation/MikeSidePanelNavigation';
import MikeSidePanelNavigationItem from '../mike-sidepanel-navigationitem/MikeSidePanelNavigationItem';
import { GlobalContext } from '../../store/Store';
import InitialDatePicker from './InitialDatePicker';
import { GlobalState } from '../app/AppWrapper';
import MikeLayerManager from './LayerManager';
import { formatDate } from '../../mike-shared-helpers/date';

interface IProps {
  onLayerChanged: (layer: number) => void;
  onSidePanelOpen: (open: boolean) => void;
  initialSidePanelTab?: TPanelId;
}
export type TPanelId = 'upload-panel' | 'boundaries' | 'datalink-wizard' | 'export-panel';
interface IPanelIds {
  [key: string]: TPanelId;
}
const PANEL_IDS: IPanelIds = {
  UPLOAD_PANEL: 'upload-panel',
  BOUNDARIES: 'boundaries',
  //DATA_LAYERS: 'data-layers',
  DATALINK_WIZARD: 'datalink-wizard',
  EXPORT_PANEL: 'export-panel',
};

const SidePanel = (props: IProps) => {
  const { initialSidePanelTab } = props;
  const [activePanelId, setActivePanelId] = useState<TPanelId>();
  const [selectedTab, setselectedTab] = useState('');
  const [_gs] = useContext(GlobalContext);
  const globalState: GlobalState = _gs;

  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  useEffect(() => {
    if (initialSidePanelTab) {
      setActivePanelId(initialSidePanelTab);
      setSidePanelOpen(true);
      props.onSidePanelOpen(true);
    }
  }, [initialSidePanelTab]);

  function openSidePanel(state) {
    setSidePanelOpen(state);
    props.onSidePanelOpen(state);
  }

  function setActivePanel(panelID: TPanelId) {
    setActivePanelId(panelID);
  }

  function getPanelTitle() {
    switch (activePanelId) {
      case PANEL_IDS.UPLOAD_PANEL:
        return 'Import mesh';
      case PANEL_IDS.BOUNDARIES:
        return 'Data extraction settings';
      // case PANEL_IDS.DATA_LAYERS:
      //   return 'Data Layers';
      case PANEL_IDS.DATALINK_WIZARD:
        return 'Data selection';
      case PANEL_IDS.EXPORT_PANEL:
        return 'Export';
    }
    return '';
  }

  const selectedTabCallBack = value => {
    setselectedTab(value);
  };

  const getNavigation = () => {
    return (
      <MikeSidePanelNavigation onNavigationChange={setActivePanel}>
        <MikeSidePanelNavigationItem
          tooltipTitle="Import mesh"
          tooltipPlacement="left"
          value={PANEL_IDS.UPLOAD_PANEL}
          icon={<Upload />}
        />
        <MikeSidePanelNavigationItem
          value={PANEL_IDS.BOUNDARIES}
          tooltipTitle="Data extraction settings" 
          tooltipPlacement="left"
          icon={<CalendarSetting />}
        />
        <MikeSidePanelNavigationItem
          value={PANEL_IDS.DATALINK_WIZARD}
          tooltipTitle="Data selection"
          tooltipPlacement="left"
          icon={<ProcessedData />}
        />
        <MikeSidePanelNavigationItem
          value={PANEL_IDS.EXPORT_PANEL}
          tooltipTitle="Export"
          tooltipPlacement="left"
          icon={<Download />}
        />
      </MikeSidePanelNavigation>
    );
  };

  const panelTitleSmall =
    activePanelId === PANEL_IDS.UPLOAD_PANEL || activePanelId === PANEL_IDS.BOUNDARIES
      ? 'panelTitleSmall'
      : '';
  const divClassName1 =
    activePanelId === PANEL_IDS.EXPORT_PANEL ? `panelTitleExport` : `panelTitle`;
  const divClassName2 = `${activePanelId} ${divClassName1} ${panelTitleSmall}`;
  const hideModelType =
    activePanelId === PANEL_IDS.UPLOAD_PANEL || activePanelId === PANEL_IDS.BOUNDARIES;

  return (
    <>
      <MikeLayerManager sidePanelState={sidePanelOpen} onLayerSelected={props.onLayerChanged} />
      <MikeSidePanel
        className={'SliderMenuExtraStyle ' + (sidePanelOpen ? 'open' : 'closed')}
        defaultOpen={true}
        onChange={openSidePanel}
        navigation={getNavigation()}
        fullHeight>
        <div className={divClassName2}>
          <Typography variant="h3">{getPanelTitle()}</Typography>
          {!hideModelType && (
            <div className="panelTitleSummery">Model type: {globalState.mesh.modelType}</div>
          )}
          <span
            className={
              (selectedTab !== 'initial' && activePanelId === PANEL_IDS.DATALINK_WIZARD) ||
              activePanelId === PANEL_IDS.EXPORT_PANEL
                ? ''
                : 'hidePanel'
            }>
            <div className="panelTitleSummery">
              Time period: {formatDate(globalState.mesh.extractionStartDate) + " - " + formatDate(globalState.mesh.extractionEndDate)}
            </div>
          </span>

          <span
            className={
              selectedTab === 'initial' && activePanelId === PANEL_IDS.DATALINK_WIZARD
                ? ''
                : 'hidePanel'
            }>
            <div className="panelTitlePicker">
              <span className='InitialConditionsLabel'>Date for initial conditions:</span>
              <span className="InitialDatePicker">
                <InitialDatePicker />
              </span>
            </div>
          </span>

          {activePanelId === PANEL_IDS.EXPORT_PANEL && (
            <div className="panelTitleSummery">
              <span>Initial condition date: </span>
              <span>{formatDate(globalState.mesh.extractionInitialPeriod)}</span>
            </div>
          )}
        </div>

        <div className={activePanelId === PANEL_IDS.UPLOAD_PANEL ? '' : 'hidePanel'}>
          <ImportMeshPanel />
        </div>
        <div className={activePanelId === PANEL_IDS.BOUNDARIES ? '' : 'hidePanel'}>
          <ExtractionSettingsPanel />
        </div>
        <div className={activePanelId === PANEL_IDS.DATALINK_WIZARD ? '' : 'hidePanel'}>
          <DataSelectionPanel selectedTab={selectedTabCallBack} />
        </div>
        <div
          className={
            activePanelId === PANEL_IDS.EXPORT_PANEL ? 'showPanelFullHeight' : 'hidePanel'
          }>
          <MenuExport />
        </div>
      </MikeSidePanel>
    </>
  );
};

export default SidePanel;
